import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { MdOutlineSms } from 'react-icons/md';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { PhoneInput } from 'components/ui/phone-input';
import useOTPLogin from 'hooks/useOTPLogin';
import { useState } from 'react';
import { OTPChannel } from 'services/auth/type';
import { cn } from 'lib/utils';

export default function PhoneOTPSignin() {
  const { t } = useTranslation();

  const [selectedChannel, setSelectedChannel] =
    useState<OTPChannel>('whatsapp');

  const {
    isOtpSent,
    userName,
    session,
    isLoading,
    mutate,
    setIsLoading,
    resendOTP,
    isResending,
    isNewUser,
    mutateSignUp,
    resendSignupOtp,
    setIsOtpSent,
  } = useOTPLogin('phone');

  const validator = z.object({
    phone: z.string().min(1, {
      message: 'Please enter phone number.',
    }),
    otp: isOtpSent
      ? z.string().min(1, { message: 'Please provide OTP' })
      : z.string().optional(),
  });

  const form = useForm<z.infer<typeof validator>>({
    resolver: zodResolver(validator),
    defaultValues: {
      phone: '',
      otp: '',
    },
  });

  const onSubmit = (values: z.infer<typeof validator>) => {
    setIsLoading(true);
    if (isNewUser) {
      return mutateSignUp({
        otp_value: values.otp!,
        username: userName,
        session,
        channel: selectedChannel,
      });
    }

    if (isOtpSent) {
      mutate({
        username: userName,
        auth_type: 'verify_auth_otp',
        session,
        otp_value: values.otp,
        channel: selectedChannel,
      });
    } else {
      mutate({
        username: values.phone,
        auth_type: 'otp',
        channel: selectedChannel,
      });
    }
  };

  return (
    <div className='space-y-8'>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='w-full space-y-8'
        >
          <FormField
            control={form.control}
            name='phone'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('enter_phone_number')}</FormLabel>
                <FormControl>
                  <PhoneInput defaultCountry='MX' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isOtpSent && (
            <div>
              <FormField
                control={form.control}
                name='otp'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('otp')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => {
                          const { value } = e.target;
                          const numericRegex = /^\d{0,6}$/;
                          if (numericRegex.test(value)) {
                            field.onChange(value);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className='flex justify-end'>
                <Button
                  type='button'
                  variant='link'
                  disabled={isLoading || isResending}
                  onClick={() => {
                    if (isNewUser) {
                      resendSignupOtp(form.getValues('phone'));
                    } else {
                      resendOTP(form.getValues('phone'));
                    }
                  }}
                >
                  {isResending && (
                    <Loader2
                      role='spinbutton'
                      className='mr-2 size-4 animate-spin'
                    />
                  )}
                  {t('resent_otp')}
                </Button>
              </div>
            </div>
          )}

          <div className='flex flex-col justify-center gap-2'>
            <Button
              type='submit'
              className='gap-2 px-6 font-semibold rounded-full'
              disabled={!form.formState.isValid || isLoading}
            >
              {isLoading && (
                <Loader2
                  role='progressbar'
                  className='mr-2 size-4 animate-spin'
                />
              )}
              <span>{isOtpSent ? t('verify_otp') : t('get_otp')}</span>
              {selectedChannel === 'whatsapp' ? (
                <FaWhatsapp size={20} />
              ) : (
                <MdOutlineSms size={20} />
              )}
            </Button>
            <Button
              variant='link'
              onClick={() => {
                setIsOtpSent(false);
                form.reset();
                setSelectedChannel((prev) =>
                  prev === 'whatsapp' ? 'sms' : 'whatsapp'
                );
              }}
              className='gap-2'
              type='button'
            >
              {selectedChannel === 'whatsapp' ? (
                <MdOutlineSms size={20} />
              ) : (
                <FaWhatsapp size={20} />
              )}
              <span className={cn(selectedChannel === 'whatsapp' && 'pb-0.5')}>
                {selectedChannel === 'whatsapp'
                  ? t('sms_otp_message')
                  : t('whatsapp_otp_message')}
              </span>
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
